import React, { useContext, useState, useRef, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';
import ThemeContext from '../../contexts/ThemeContext';
import axios from 'axios';
import { getFileUrl } from '../../utils/fileUtil';
import { pushSupported, pushPermission, enablePushNotifications } from '../../utils/pushConfig';
import GlobalAIAssistant from '../common/GlobalAIAssistant';
import GlobalSearch from '../common/GlobalSearch';

const MainLayout = () => {
  const { currentUser, logout, isAdmin, token } = useContext(AuthContext);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [pushPerm, setPushPerm] = useState(pushPermission()); // 'granted'|'denied'|'default'|'unsupported'
  const dropdownRef = useRef(null);
  const notificationsRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Branding State
  const [brand, setBrand] = useState({
    appName: 'Frugal Prints Kanban',
    logo: '',
    favicon: '',
    primaryColor: '#1a73e8'
  });

  const isBoardView = location.pathname.startsWith('/boards/');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const fetchNotifications = async () => {
    if (!token) return;
    try {
      const res = await axios.get('/api/notifications', {
        headers: { 'x-auth-token': token }
      });
      setNotifications(res.data);
    } catch (err) {
      console.error('Error fetching notifications');
    }
  };

  const fetchBrandSettings = async () => {
    try {
      const res = await axios.get('/api/brand');
      setBrand(res.data);
      
      // Update Tab Title
      document.title = res.data.appName || 'Frugal Prints Kanban';
      
      // Update Favicon with robust selector
      if (res.data.favicon) {
        const faviconUrl = getFileUrl(res.data.favicon);
        const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'shortcut icon';
        link.href = faviconUrl;
        if (!document.querySelector("link[rel*='icon']")) {
          document.getElementsByTagName('head')[0].appendChild(link);
        }
      }
    } catch (err) {
      console.error('Error fetching brand settings');
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchBrandSettings();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`/api/notifications/${id}/read`, {}, {
        headers: { 'x-auth-token': token }
      });
      setNotifications(notifications.map(n => n._id === id ? { ...n, read: true } : n));
    } catch (err) {
      console.error(err);
    }
  };

  const handleNotificationClick = (n) => {
    markAsRead(n._id);
    setNotificationsOpen(false);
    if (n.link) navigate(n.link);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-950 font-sans antialiased text-gray-900 dark:text-gray-100">
      {/* Dynamic Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-sm z-30">
        <div className={isBoardView ? "px-6" : "container mx-auto px-4"}>
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="flex items-center group">
                {brand.logo ? (
                  <img src={getFileUrl(brand.logo)} alt={brand.appName} className="h-8 w-auto mr-3 transition-transform group-hover:scale-105" />
                ) : (
                  <div className="h-8 w-8 bg-primary-blue rounded-lg mr-3 flex items-center justify-center text-white font-black transition-transform group-hover:rotate-12">K</div>
                )}
                <span className="text-xl font-black tracking-tighter text-gray-900 dark:text-gray-100 uppercase">
                  {brand.appName}
                </span>
              </Link>
              {currentUser && (
                <div className="hidden md:block relative ml-6 group">
                  <Link
                    to="/daily-plan"
                    className="inline-flex items-center text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 group-hover:text-primary-blue transition-colors">
                    Planning
                    <svg className="ml-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </Link>
                  <div className="absolute left-0 top-full pt-3 hidden group-hover:block z-50">
                    <div className="w-44 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl py-2 ring-1 ring-black/5 dark:ring-white/10 animate-fade-in">
                      <Link
                        to="/daily-plan"
                        className="block px-4 py-3 text-sm font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-blue dark:hover:text-blue-400 transition-colors">
                        Daily Plan
                      </Link>
                      <Link
                        to="/calendar"
                        className="block px-4 py-3 text-sm font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-blue dark:hover:text-blue-400 transition-colors">
                        Calendar
                      </Link>
                      <Link
                        to="/mindmaps"
                        className="block px-4 py-3 text-sm font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-blue dark:hover:text-blue-400 transition-colors">
                        Mind Maps
                      </Link>
                      <Link
                        to="/flowmaps"
                        className="block px-4 py-3 text-sm font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-blue dark:hover:text-blue-400 transition-colors">
                        Flowcharts
                      </Link>
                      <Link
                        to="/org-chart"
                        className="block px-4 py-3 text-sm font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-blue dark:hover:text-blue-400 transition-colors">
                        Org Chart
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              {currentUser && (
                <Link
                  to="/reports"
                  className="hidden md:inline-flex ml-4 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 hover:text-primary-blue transition-colors">
                  Reports
                </Link>
              )}
              {/* Ads moves real spend, so it is admin-only here and on the API. */}
              {currentUser && isAdmin && (
                <Link
                  to="/ads"
                  className="hidden md:inline-flex ml-4 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 hover:text-primary-blue transition-colors">
                  Ads
                </Link>
              )}
              {/* Campaign Builder pushes to Trello/email/Ads, so admin-only too. */}
              {currentUser && isAdmin && (
                <Link
                  to="/campaigns"
                  className="hidden md:inline-flex ml-4 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 hover:text-primary-blue transition-colors">
                  Campaigns
                </Link>
              )}
              {/* Team goals read and write live BambooHR data: admin-only. */}
              {currentUser && isAdmin && (
                <Link
                  to="/team-goals"
                  className="hidden md:inline-flex ml-4 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 hover:text-primary-blue transition-colors">
                  Goals
                </Link>
              )}
              {/* Intake forms open a public front door onto a board: admin-only. */}
              {currentUser && isAdmin && (
                <Link
                  to="/forms"
                  className="hidden md:inline-flex ml-4 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 hover:text-primary-blue transition-colors">
                  Forms
                </Link>
              )}
            </div>

            <div className="flex items-center space-x-6">
              {/* Global Search — icon that opens a search panel */}
              {currentUser && <GlobalSearch />}

              {/* Dark mode toggle */}
              <button
                onClick={toggleTheme}
                aria-label="Toggle dark mode"
                className="p-2 text-gray-400 hover:text-primary-blue hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-all focus:outline-none"
              >
                {theme === 'dark' ? (
                  /* Sun icon — shown in dark mode to switch to light */
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="5" />
                    <line x1="12" y1="1" x2="12" y2="3" />
                    <line x1="12" y1="21" x2="12" y2="23" />
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                    <line x1="1" y1="12" x2="3" y2="12" />
                    <line x1="21" y1="12" x2="23" y2="12" />
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                  </svg>
                ) : (
                  /* Moon icon — shown in light mode to switch to dark */
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                  </svg>
                )}
              </button>

              {currentUser && (
                <>
                  <div className="relative" ref={notificationsRef}>
                    <button 
                      onClick={() => setNotificationsOpen(!notificationsOpen)}
                      className="relative p-2 text-gray-400 hover:text-primary-blue hover:bg-gray-50 rounded-xl transition-all focus:outline-none"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      {unreadCount > 0 && (
                        <span className="absolute top-1 right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-black leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full border-2 border-white">
                          {unreadCount}
                        </span>
                      )}
                    </button>

                    {notificationsOpen && (
                      <div className="absolute right-0 mt-3 w-80 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl py-2 z-50 ring-1 ring-black/5 dark:ring-white/10 animate-fade-in">
                        <div className="px-4 py-3 border-b border-gray-50 dark:border-gray-700 flex justify-between items-center">
                          <span className="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Alerts</span>
                          {unreadCount > 0 && (
                            <button 
                              onClick={async () => {
                                await axios.put('/api/notifications/read-all', {}, { headers: { 'x-auth-token': token } });
                                setNotifications(notifications.map(n => ({ ...n, read: true })));
                              }}
                              className="text-[10px] font-bold text-primary-blue dark:text-blue-400 hover:underline"
                            >
                              Clear all
                            </button>
                          )}
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                          {notifications.length === 0 ? (
                            <div className="px-4 py-8 text-center text-xs text-gray-400 dark:text-gray-500 font-bold uppercase tracking-tighter">No new alerts</div>
                          ) : (
                            notifications.map(n => (
                              <div 
                                key={n._id} 
                                onClick={() => handleNotificationClick(n)}
                                className={`px-4 py-4 hover:bg-gray-50/80 dark:hover:bg-gray-700/80 cursor-pointer border-b border-gray-50 dark:border-gray-700 flex items-start space-x-3 transition-colors ${!n.read ? 'bg-blue-50/20 dark:bg-blue-950/20' : ''}`}
                              >
                                <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-gray-700 flex-shrink-0 overflow-hidden border-2 border-white dark:border-gray-800 shadow-sm">
                                  {n.sender?.avatar ? (
                                    <img src={getFileUrl(n.sender.avatar)} alt="" className="h-full w-full object-cover" />
                                  ) : (
                                    <div className="h-full w-full flex items-center justify-center bg-secondary-cyan text-white text-xs font-bold uppercase">
                                      {n.sender?.name?.charAt(0)}
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-xs leading-relaxed ${!n.read ? 'font-bold text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>{n.message}</p>
                                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 font-bold">{new Date(n.createdAt).toLocaleDateString()}</p>
                                </div>
                                {!n.read && <div className="h-2 w-2 bg-primary-blue rounded-full mt-2 flex-shrink-0 ring-4 ring-blue-100/50"></div>}
                              </div>
                            ))
                          )}
                        </div>
                        {pushSupported() && pushPerm !== 'granted' && (
                          <div className="px-4 py-3 border-t border-gray-50 dark:border-gray-700">
                            {pushPerm === 'denied' ? (
                              <p className="text-[10px] text-gray-400 dark:text-gray-500 font-bold leading-relaxed">
                                Push is blocked for this site. Enable notifications for Projects in your browser/phone settings to get alerts here.
                              </p>
                            ) : (
                              <button
                                onClick={async () => { const p = await enablePushNotifications(); setPushPerm(p); }}
                                className="w-full py-2 rounded-xl text-[11px] font-black uppercase tracking-widest text-white bg-primary-blue hover:opacity-90 transition-all"
                              >
                                🔔 Enable push on this device
                              </button>
                            )}
                            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2 font-bold leading-relaxed">
                              On iPhone: add Projects to your Home Screen first (Share → Add to Home Screen), then open it and tap Enable.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="hidden md:flex items-center space-x-3 pr-2 border-r border-gray-100 dark:border-gray-700">
                    <div className="text-right">
                      <p className="text-sm font-black text-gray-900 dark:text-gray-100 leading-none uppercase tracking-tighter">{currentUser.name}</p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest mt-1">{currentUser.role}</p>
                    </div>
                    <div className="h-10 w-10 rounded-full bg-secondary-cyan flex items-center justify-center overflow-hidden border-2 border-white shadow-md">
                      {currentUser.avatar ? (
                        <img 
                          src={getFileUrl(currentUser.avatar)} 
                          alt={currentUser.name} 
                          className="h-full w-full object-cover" 
                        />
                      ) : (
                        <span className="text-white font-black text-lg">
                          {currentUser.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="relative" ref={dropdownRef}>
                    <button 
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className="flex items-center focus:outline-none p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-all"
                    >
                      <svg className={`h-5 w-5 transform transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {dropdownOpen && (
                      <div className="absolute right-0 mt-3 w-56 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl py-2 z-50 ring-1 ring-black/5 dark:ring-white/10 animate-fade-in">
                        <div className="px-4 py-2 mb-2 border-b border-gray-50 dark:border-gray-700">
                          <p className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Account</p>
                        </div>
                        <Link 
                          to="/profile" 
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center px-4 py-3 text-sm font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-blue dark:hover:text-blue-400 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" strokeWidth="2" strokeLinecap="round"/></svg>
                          My Profile
                        </Link>

                        <Link 
                          to="/teams" 
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center px-4 py-3 text-sm font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-blue dark:hover:text-blue-400 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" strokeWidth="2" strokeLinecap="round"/></svg>
                          Manage Teams
                        </Link>
                        
                        {isAdmin && (
                          <Link 
                            to="/admin" 
                            onClick={() => setDropdownOpen(false)}
                            className="flex items-center px-4 py-3 text-sm font-bold text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/30 transition-colors"
                          >
                            <svg className="w-4 h-4 mr-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" strokeWidth="2" strokeLinecap="round"/><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeWidth="2"/></svg>
                            Admin Console
                          </Link>
                        )}
                        
                        <hr className="my-2 border-gray-50 dark:border-gray-700" />
                        
                        <button
                          onClick={() => {
                            setDropdownOpen(false);
                            handleLogout();
                          }}
                          className="flex items-center w-full text-left px-4 py-3 text-sm font-bold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          Sign out
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className={`flex-1 ${isBoardView ? "" : "container mx-auto px-4 py-8"}`} >
        <Outlet />
      </main>
      
      {/* Footer */}
      {!isBoardView && (
        <footer className="bg-white dark:bg-gray-900 py-8 border-t border-gray-100 dark:border-gray-700">
          <div className="container mx-auto px-4 text-center">
            <p className="text-[10px] font-black text-gray-300 uppercase tracking-[0.2em]">
              &copy; {new Date().getFullYear()} Frugal Prints
            </p>
          </div>
        </footer>
      )}

      {currentUser && <GlobalAIAssistant />}
    </div>
  );
};

export default MainLayout;